/**
 * MosaicGrowthCard.js — v6.32.166
 *
 * Predictive growth view for a single student. Pulls from
 * GET /api/mosaic/growth/{student_id} and renders:
 *   - Current composite + level chip
 *   - Linear-regression weekly growth rate vs the 0.05/week target
 *   - On-track / Off-pace status pill
 *   - Inline SVG sparkline of historical + projected composite
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import MosaicBucketBadge from './MosaicBucketBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sparkline = ({ history = [], projections = [] }) => {
  const allPts = [
    ...history.map((h, i) => ({ x: i, y: h.composite, kind: 'hist' })),
    ...projections.map((p, i) => ({ x: history.length + i, y: p.projected_composite, kind: 'proj' })),
  ];
  if (allPts.length < 2) {
    return <div className="text-[11px] italic text-gray-400 p-3">Not enough history yet — at least 2 data points are needed for a projection.</div>;
  }
  const W = 400, H = 80, PAD = 6;
  const minY = 1, maxY = 5;
  const xs = allPts.map((p) => p.x);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const toX = (x) => PAD + ((x - minX) / Math.max(1, maxX - minX)) * (W - 2 * PAD);
  const toY = (y) => H - PAD - ((y - minY) / (maxY - minY)) * (H - 2 * PAD);
  const histPath = history.map((h, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(h.composite)}`).join(' ');
  const projPath = (history.length > 0 && projections.length > 0)
    ? `M ${toX(history.length - 1)} ${toY(history[history.length - 1].composite)} ` +
      projections.map((p, i) => `L ${toX(history.length + i)} ${toY(p.projected_composite)}`).join(' ')
    : '';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20" data-testid="growth-sparkline">
      {/* Level gridlines */}
      {[1, 2, 3, 4, 5].map((l) => (
        <line key={l} x1={PAD} x2={W - PAD} y1={toY(l)} y2={toY(l)} stroke="#e5e7eb" strokeWidth="0.5" />
      ))}
      {histPath && <path d={histPath} fill="none" stroke="#7c3aed" strokeWidth="2" />}
      {projPath && <path d={projPath} fill="none" stroke="#7c3aed" strokeWidth="2" strokeDasharray="3 3" opacity="0.6" />}
      {history.map((h, i) => (
        <circle key={`h${i}`} cx={toX(i)} cy={toY(h.composite)} r="2.5" fill="#7c3aed" />
      ))}
      {projections.map((p, i) => (
        <circle key={`p${i}`} cx={toX(history.length + i)} cy={toY(p.projected_composite)} r="2" fill="#a78bfa" />
      ))}
    </svg>
  );
};

const MosaicGrowthCard = ({ studentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/mosaic/growth/${studentId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load growth'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <div className="text-xs text-gray-500 p-3 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading growth…</div>;
  if (error) return <div className="text-xs text-rose-700 p-3">{error}</div>;
  if (!data || !data.current) {
    return (
      <div className="border border-gray-200 p-3 text-xs text-gray-500 italic" data-testid="mosaic-growth-card">
        No growth history yet. Run a screener or save intake levels to start tracking.
      </div>
    );
  }

  const trend = data.weekly_growth > 0.001 ? 'up' : data.weekly_growth < -0.001 ? 'down' : 'flat';
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendClasses = trend === 'up'
    ? 'text-emerald-700 border-emerald-300 bg-emerald-50'
    : trend === 'down' ? 'text-rose-700 border-rose-300 bg-rose-50'
    : 'text-gray-600 border-gray-300 bg-gray-50';

  return (
    <div className="border-2 border-violet-300 bg-violet-50/30" data-testid="mosaic-growth-card">
      <div className="px-3 py-2 bg-violet-700 text-white flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-widest">Predicted Growth</div>
        <span
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${data.on_track ? 'border-emerald-300 text-emerald-100 bg-emerald-800/30' : 'border-amber-300 text-amber-100 bg-amber-800/30'}`}
          data-testid="growth-on-track"
        >
          {data.on_track ? 'On Track' : 'Off Pace'}
        </span>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Current</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data.current.composite}
              </span>
              <MosaicBucketBadge level={data.current.level} compact />
            </div>
          </div>
          <div className={`ml-auto inline-flex items-center gap-1 px-2 py-1 border ${trendClasses}`} data-testid="growth-rate">
            <TrendIcon className="w-3 h-3" />
            <span className="text-xs font-bold tabular-nums">
              {data.weekly_growth >= 0 ? '+' : ''}{data.weekly_growth.toFixed(3)}/wk
            </span>
            <span className="text-[10px] text-gray-500">target {data.target_weekly_growth}</span>
          </div>
        </div>

        <Sparkline history={data.history || []} projections={data.projections || []} />

        {(data.projections || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-1" data-testid="growth-projections">
            {data.projections.map((p) => (
              <div key={p.weeks_ahead} className="border border-violet-200 bg-white p-2">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">+{p.weeks_ahead}w</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-bold tabular-nums">{p.projected_composite}</span>
                  <MosaicBucketBadge level={p.projected_level} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MosaicGrowthCard;
